import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { useNow } from "@/hooks/useNow";
import { api, type BillingOverview } from "@/lib/api";
import { formatMoney, relativeIso } from "@/lib/format";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CreditCard, Pencil } from "lucide-react";
import { useState } from "react";

import { AddCreditsDialog } from "./AddCreditsDialog";
import { AutoRechargeDialog } from "./AutoRechargeCard";
import { TransactionsSection } from "./TransactionsSection";

// The Usage page's Credit balance tab: the balance card carrying the saved
// payment method and the buy/auto-recharge actions, then the money movements.

const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

function CreditBalanceSection({
  overview,
  dataUpdatedAt,
}: {
  overview: BillingOverview;
  dataUpdatedAt: number;
}) {
  const now = useNow();

  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [autoRechargeOpen, setAutoRechargeOpen] = useState(false);

  const canPurchase = overview._permissions?.canPurchaseCredits !== false;
  const canManage = overview._permissions?.canManageAutoRecharge !== false;

  const autoRecharge = useQuery({
    queryKey: ["billing-auto-recharge"],
    queryFn: api.billing.autoRecharge,
    enabled: canManage,
  });
  const setup = useMutation({
    mutationFn: api.billing.setupPaymentMethod,
    onSuccess: ({ checkoutUrl }) => window.location.assign(checkoutUrl),
  });

  const ar = autoRecharge.data;
  const isStale = dataUpdatedAt > 0 && now - dataUpdatedAt > STALE_THRESHOLD_MS;

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">Credit balance</h2>
        <p className="text-sm text-muted-foreground">
          Your credit balance is consumed as your Boos work. You can buy credits
          directly or set up auto-recharge.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border p-5">
        <div>
          <div className="text-3xl font-semibold tracking-tight">
            {formatMoney(overview.creditBalance)}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Remaining balance
            {dataUpdatedAt > 0 && (
              <>
                {" · "}
                <span
                  className={
                    isStale
                      ? "font-semibold text-amber-600 dark:text-amber-400"
                      : undefined
                  }
                  title={new Date(dataUpdatedAt).toLocaleString()}
                >
                  Updated {relativeIso(new Date(dataUpdatedAt).toISOString())}
                </span>
              </>
            )}
            {canManage && ar && (
              <>
                {" · "}
                <button
                  type="button"
                  onClick={() => setAutoRechargeOpen(true)}
                  className="text-primary underline underline-offset-4 hover:no-underline"
                >
                  Auto-recharge {ar.enabled ? "on" : "off"}
                </button>
              </>
            )}
          </div>
          <div className="mt-1 text-sm tabular-nums text-muted-foreground">
            Spent this month: {formatMoney(overview.currentMonth.total)}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Wait for the config to resolve before rendering the card chip —
              otherwise a slow fetch flashes "Add payment method" at users who
              already saved a card. */}
          {canManage &&
            autoRecharge.isSuccess &&
            (ar?.paymentMethod ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <CreditCard className="size-4 text-muted-foreground" />
                <span>
                  <span className="capitalize">{ar.paymentMethod.brand}</span> ••••{" "}
                  {ar.paymentMethod.last4}
                </span>
                <button
                  type="button"
                  aria-label="Replace card"
                  onClick={() => setup.mutate()}
                  disabled={setup.isPending}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="size-3.5" />
                </button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setup.mutate()}
                disabled={setup.isPending}
              >
                {setup.isPending ? "Redirecting…" : "Add payment method"}
              </Button>
            ))}
          {canPurchase && <Button onClick={() => setAddCreditsOpen(true)}>Buy credits</Button>}
        </div>
      </div>

      <AddCreditsDialog open={addCreditsOpen} onOpenChange={setAddCreditsOpen} />
      {ar && (
        <AutoRechargeDialog
          open={autoRechargeOpen}
          onOpenChange={setAutoRechargeOpen}
          config={ar}
        />
      )}
    </section>
  );
}

export function CreditBalanceTab() {
  const overview = useQuery({ queryKey: ["billing-overview"], queryFn: api.billing.overview });

  if (overview.isPending) return <LoadingIndicator />;
  if (overview.isError) return <ErrorComponent error={overview.error} />;

  return (
    <div className="space-y-8">
      <CreditBalanceSection overview={overview.data} dataUpdatedAt={overview.dataUpdatedAt} />
      {/* Prepaid rollout gate: orgs outside the allowlist get 403 from the
          transactions endpoint, so don't render (or query) the section. */}
      {overview.data._permissions?.canViewTransactions !== false && <TransactionsSection />}
    </div>
  );
}
